import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Carousel from "segments/desktop/core-components/carousel/carousel";
import TabContentComponent from "segments/desktop/tabs/layouts/carousel-tabs/components/tab-content/tab-content-component";
import { CAROUSEL_TAB_CLICKED } from "constants/bigbrain-event-types";
import { trackEvent } from "services/bigbrain-service";
import { useSlidesToShow } from "segments/desktop/tabs/layouts/carousel-tabs/custom-hooks/use-slides-to-show";
import TabsLabelsComponent from "./components/tabs-labels/tabs-labels-component";
import { StyledCarouselTabsComponent } from "./carousel-tabs-component.scss";

export const CAROUSEL_SETTINGS = {
  defaultIndex: 0,
  infinite: false,
  centerMode: false,
  swipe: false,
  draggable: false,
  dots: false
};

const CarouselTabsComponent = ({
  tabs,
  transitionTime,
  progressbarColor,
  tabAdvancedConfig,
  isInView,
  containerGap,
  tabLabelHeight,
  autoPlayWhenVisible,
  advancedAssetProps
}) => {
  const slidesToShow = useSlidesToShow();
  const slider = useRef(null);
  const parsedTransitionTime = parseInt(transitionTime);
  const [selectedTab, setSelectedTab] = useState(0);

  const labels = useMemo(() => tabs?.map((tab) => tab.tabLabel), [tabs]);
  const slides = useMemo(() => tabs?.map((tab) => tab.tabContent), [tabs]);

  const onSelectionChanges = useCallback((index) => {
    setSelectedTab(index);
    trackEvent(CAROUSEL_TAB_CLICKED, { info1: index + 1 });
  }, []);

  useEffect(() => {
    if (!parsedTransitionTime || (autoPlayWhenVisible && !isInView)) return;

    const interval = setInterval(() => {
      setSelectedTab((selectedTab + 1) % tabs?.length);
    }, parsedTransitionTime);

    return () => {
      clearInterval(interval);
    };
  }, [tabs?.length, parsedTransitionTime, isInView, autoPlayWhenVisible, selectedTab]);

  useEffect(() => {
    slider?.current.scrollToSlide(selectedTab);
  }, [selectedTab]);

  const tabRenderer = useCallback(
    (item) => (
      <TabContentComponent {...item} tabAdvancedConfig={tabAdvancedConfig} advancedAssetProps={advancedAssetProps} />
    ),
    [advancedAssetProps, tabAdvancedConfig]
  );

  const onSlideChange = (index) => {
    const didChangedByClick = index !== selectedTab;
    if (didChangedByClick) {
      setSelectedTab(index);
      trackEvent(CAROUSEL_TAB_CLICKED, { info1: index + 1 });
    }
  };

  return (
    <StyledCarouselTabsComponent containerGap={containerGap} className="carousel-tabs-component">
      <div className="carousel-tabs-container">
        <TabsLabelsComponent
          tabLabelHeight={tabLabelHeight}
          slidesToShow={slidesToShow}
          selectedLabelIndex={selectedTab}
          transitionTime={autoPlayWhenVisible && !isInView ? null : parsedTransitionTime}
          onSelectionChanges={onSelectionChanges}
          progressbarColor={progressbarColor}
          labels={labels}
        />
        <Carousel
          onChange={onSlideChange}
          slidesToShow={slidesToShow}
          itemRenderer={tabRenderer}
          items={slides}
          ref={slider}
          {...CAROUSEL_SETTINGS}
        />
      </div>
    </StyledCarouselTabsComponent>
  );
};

export default CarouselTabsComponent;
